/** Takes every input as an argument so each surface passes what it has and the hook stays testable. */

import { useCallback, useEffect, useMemo, useState } from "react";
import { trackWebAppPromoClicked, trackWebAppPromoViewed } from "../PixelTrackers/webAppPromo";
import useSeenOnScreen from "./useSeenOnScreen";
import { APP_LINK_BASE_URL, buildAppLink } from "../utils/appLink";
import {
  APP_PLACEMENTS,
  AppPlacementId,
  getAppPlatform,
  getAppDeepLinkValue,
  isAppPlacementCountry,
} from "../utils/appPlacements";
import {
  AppLinkVisitResult,
  getAppLinkVisit,
  getAppLinkVisitMissing,
  getAppLinkVisitSkipReason,
  saveAppLinkVisit,
} from "../utils/appLinkVisit";

const DEFAULT_SEGMENT = "logged_in_de";

export interface UseAppPlacementArgs {
  placementId: AppPlacementId;
  /** Country from the URL, not language, so de-CH does not qualify. */
  country?: string | null;
  /** Null or empty when logged out. */
  customerUuid?: string | null;
  /** Undefined while loading keeps it hidden, so a paired customer never sees a flash or an impression. */
  isPairedToMobileApp?: boolean;
  /** Ids the api allows; undefined while loading or from an older api, which keeps the placement hidden. */
  allowedPlacements?: readonly string[];
  orderHash?: string | null;
  segment?: string;
  /** False when rendered but off screen (collapsed accordion), so an unseen row logs no impression. */
  inView?: boolean;
  /** Reported as `app_cta` because the CTA follows the device while the placement id follows width. */
  cta?: "qr" | "badges";
  /** Reported as `cta_text` on click. */
  ctaText: string;
}

export interface UseAppPlacementResult {
  visible: boolean;
  href: string;
  qrValue: string | null;
  onClick: () => void;
  /** Goes on the placement's root so the view is counted only once it is half on screen. */
  ref: (element: Element | null) => void;
}

const useAppPlacement = ({
  placementId,
  country,
  customerUuid,
  isPairedToMobileApp,
  allowedPlacements,
  segment = DEFAULT_SEGMENT,
  inView = true,
  cta,
  ctaText,
}: UseAppPlacementArgs): UseAppPlacementResult => {
  const config = APP_PLACEMENTS[placementId];

  const eligible =
    isAppPlacementCountry(country) &&
    Boolean(customerUuid) &&
    isPairedToMobileApp === false &&
    allowedPlacements?.includes(placementId) === true;
  // Without the banners link the CTA would lead nowhere, so hide it; buildAppLink logs the warning.
  const visible = eligible && Boolean(APP_LINK_BASE_URL);

  const trackingProps = useMemo(
    () => ({
      app_placement_id: placementId,
      app_platform: getAppPlatform(),
      app_page: config.page,
      app_user_segment: segment,
      ...(cta ? { app_cta: cta } : {}),
    }),
    [placementId, config.page, segment, cta]
  );

  // The breakpoint twins (W1/W2, W3/W4) share one root, so a resize counts no second view.
  const ref = useSeenOnScreen(visible && inView, () => trackWebAppPromoViewed(placementId, customerUuid, trackingProps));

  // Read once per mount: the tracker only records a new arrival on a page load.
  const visit = useMemo(() => (eligible ? getAppLinkVisit() : null), [eligible]);
  const skipReason = useMemo(
    () => (eligible ? getAppLinkVisitSkipReason(customerUuid, visit) : null),
    [eligible, customerUuid, visit]
  );
  const [saved, setSaved] = useState<AppLinkVisitResult | null>(null);
  const visitRef = skipReason ? null : saved?.ref ?? null;
  const qrReady = Boolean(skipReason || saved);

  // Gated on visible: a hidden banner neither saves nor logs.
  useEffect(() => {
    if (!visible) return;
    const logParams = { placement: placementId, page: config.page };
    const missing = getAppLinkVisitMissing(visit);
    if (missing.length) window.clientLogger?.error("App link is missing visit data", { ...logParams, missing });
    if (skipReason) return;
    let active = true;
    saveAppLinkVisit(customerUuid, visit).then((saveResult) => {
      if (!active) return;
      if (saveResult.reason === "failed") window.clientLogger?.error("App link visit save failed, QR without a ref", logParams);
      setSaved(saveResult);
    });
    return () => {
      active = false;
    };
  }, [visible, skipReason, customerUuid, visit, placementId, config.page]);

  const deepLinkValue = getAppDeepLinkValue(placementId);
  // Only for customers who would see it, so a missing link warns for them and not every visitor.
  const href = useMemo(
    () =>
      eligible ? buildAppLink({ deepLinkValue, arrival: visit?.arrival, uid: visit?.uid, ref: visitRef, placement: placementId }) : "",
    [eligible, deepLinkValue, visit, visitRef, placementId]
  );

  // The QR carries only the reference: the full visit is too long to scan at banner size.
  const qrValue = useMemo(
    () => (eligible && qrReady ? buildAppLink({ deepLinkValue, ref: visitRef, placement: placementId }) : null),
    [eligible, qrReady, deepLinkValue, visitRef, placementId]
  );

  const onClick = useCallback(() => {
    trackWebAppPromoClicked(placementId, customerUuid, ctaText, trackingProps);
  }, [placementId, customerUuid, ctaText, trackingProps]);

  return { visible, href, qrValue, onClick, ref };
};

export default useAppPlacement;
